/**
 * Profile pictures: an emoji, a small image (data URL), or a LEGO Star Wars portrait (developers).
 * `AvatarPicker` lets an expert choose an emoji or image; images are resized in the browser to 160x160
 * before upload so they fit in the profile record. Developers pick a portrait in `LegoAvatarDialog`.
 * `ProfileHover` is the profile popover on a person's name or picture (hover or click): engineers get it
 * on experts, and feedback bylines on whoever wrote the feedback.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { profileAreas, type Avatar as AvatarData, type User } from '@/lib/types'
import { fmtDay } from '@/lib/format'
import { legoAvatarUrl } from '@/lib/legoAvatars'

export function Avatar({ avatar, name, size = 32 }: { avatar?: AvatarData | null; name?: string; size?: number }) {
  const style = { width: size, height: size, fontSize: size * 0.58 }
  if (avatar?.kind === 'lego') return <img className="avatar avatar-lego" src={legoAvatarUrl(avatar.value)} alt={name ? `${name}'s picture` : 'Profile picture'} style={style} />
  if (avatar?.kind === 'image') return <img className="avatar" src={avatar.value} alt={name ? `${name}'s picture` : 'Profile picture'} style={style} />
  // The background colour comes from the server (unique per user); a not-yet-saved pick shows neutral.
  if (avatar?.kind === 'emoji') return <span className="avatar avatar-emoji" role="img" aria-label="Profile emoji" style={{ ...style, background: avatar.color ?? 'var(--bg-subtle)', borderColor: avatar.color ?? 'var(--line)' }}>{avatar.value}</span>
  return <span className="avatar avatar-blank" aria-hidden="true" style={style}>{(name?.trim()[0] ?? '?').toUpperCase()}</span>
}

const SUGGESTED = ['🦊', '🐙', '🦉', '🐝', '🦫', '🐢', '🦩', '🐬', '🌵', '🔧', '⚙️', '📐', '🧭', '🛠️', '🧪', '🚀']
const MAX_DATA_URL = 110_000

/** Downscale to a centred 160x160 JPEG data URL. */
async function toAvatarDataUrl(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file)
  const side = Math.min(bitmap.width, bitmap.height)
  const sx = (bitmap.width - side) / 2
  const sy = (bitmap.height - side) / 2
  const canvas = document.createElement('canvas')
  canvas.width = 160; canvas.height = 160
  const ctx = canvas.getContext('2d')!
  ctx.drawImage(bitmap, sx, sy, side, side, 0, 0, 160, 160)
  let quality = 0.9
  let url = canvas.toDataURL('image/jpeg', quality)
  while (url.length > MAX_DATA_URL && quality > 0.3) { quality -= 0.15; url = canvas.toDataURL('image/jpeg', quality) }
  if (url.length > MAX_DATA_URL) throw new Error('The picture is too detailed to store; try a simpler image.')
  return url
}

export function AvatarPicker({ value, onChange, name }: { value: AvatarData | null; onChange: (a: AvatarData | null) => void; name?: string }) {
  const [tab, setTab] = useState<'image' | 'emoji'>(value?.kind === 'emoji' ? 'emoji' : 'image')
  const [error, setError] = useState<string | null>(null)
  const input = useRef<HTMLInputElement>(null)

  async function onFile(f: File | undefined) {
    if (!f) return
    setError(null)
    try { onChange({ kind: 'image', value: await toAvatarDataUrl(f) }) }
    catch (e) { setError((e as Error).message) }
  }

  return (
    <div className="avatar-picker">
      <Avatar avatar={value} name={name} size={72} />
      <div className="avatar-picker-body">
        <div className="seg" role="tablist">
          <button type="button" className={tab === 'image' ? 'on' : ''} onClick={() => setTab('image')}>Picture</button>
          <button type="button" className={tab === 'emoji' ? 'on' : ''} onClick={() => setTab('emoji')}>Emoji</button>
        </div>
        {tab === 'image' ? (
          <div className="row" style={{ marginTop: '.5rem' }}>
            <button type="button" className="sm" onClick={() => input.current?.click()}>{value?.kind === 'image' ? 'Choose another picture' : 'Choose a picture'}</button>
            <span className="muted small">JPEG, PNG or WebP. It is cropped to a square and shrunk to 160 px.</span>
            <input ref={input} type="file" accept="image/*" hidden onChange={(e) => { void onFile(e.target.files?.[0]); e.target.value = '' }} />
          </div>
        ) : (
          <div style={{ marginTop: '.5rem' }}>
            <div className="emoji-grid">
              {SUGGESTED.map((e) => <button type="button" key={e} className={value?.kind === 'emoji' && value.value === e ? 'on' : ''} onClick={() => onChange({ kind: 'emoji', value: e })}>{e}</button>)}
            </div>
            <p className="muted small" style={{ marginTop: '.5rem' }}>A background colour is assigned when you save and is unique among experts.</p>
          </div>
        )}
        {error && <div className="field-error">{error}</div>}
      </div>
    </div>
  )
}

/** Hover intent: passing over a name does not open its card, and the card waits for the pointer to reach it. */
const OPEN_DELAY_MS = 150
const CLOSE_DELAY_MS = 200

/**
 * Name (and picture) that reveal a person's profile: shown while hovering, pinned open by a click
 * (outside click or Escape closes it). The card is rendered into <body> at a fixed position, so it is
 * never clipped by a scrolling table or a row that is itself a link. Leaving the name closes the card
 * only after a short delay that reaching the card cancels, so its email link can be clicked.
 */
export function ProfileHover({ user, children }: { user: Pick<User, 'name' | 'email' | 'bio' | 'avatar' | 'created_at' | 'programs' | 'domain' | 'areas'>; children: React.ReactNode }) {
  const [pinned, setPinned] = useState(false)
  const [hover, setHover] = useState(false)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const trigger = useRef<HTMLSpanElement>(null)
  const card = useRef<HTMLSpanElement>(null)
  const timer = useRef<number | undefined>(undefined)
  const visible = pinned || hover
  const cancel = () => window.clearTimeout(timer.current)
  const later = (open: boolean, delay: number) => { cancel(); timer.current = window.setTimeout(() => setHover(open), delay) }
  const enter = () => later(true, hover ? 0 : OPEN_DELAY_MS)
  const leave = () => later(false, CLOSE_DELAY_MS)
  useEffect(() => cancel, [])

  const place = () => {
    const r = trigger.current?.getBoundingClientRect()
    if (!r) return
    const width = card.current?.offsetWidth ?? Math.min(320, window.innerWidth - 16)
    const height = card.current?.offsetHeight ?? 0
    const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8))
    const below = r.bottom + 6
    const top = below + height <= window.innerHeight - 8 ? below : Math.max(8, r.top - height - 6)
    setPos({ top: Math.min(top, window.innerHeight - height - 8), left })
  }
  useLayoutEffect(() => { if (visible) place() }, [visible, user])
  useEffect(() => {
    if (!visible) return
    const onDoc = (e: MouseEvent) => { if (!trigger.current?.contains(e.target as Node) && !card.current?.contains(e.target as Node)) { cancel(); setPinned(false); setHover(false) } }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { cancel(); setPinned(false); setHover(false) } }
    document.addEventListener('mousedown', onDoc); document.addEventListener('keydown', onKey)
    window.addEventListener('scroll', place, true); window.addEventListener('resize', place)
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); window.removeEventListener('scroll', place, true); window.removeEventListener('resize', place) }
  }, [visible])

  const toggle = (e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); setPinned((v) => !v) }
  const name = user.name || user.email
  return (
    <span className="profile-hover" ref={trigger} role="button" tabIndex={0} aria-haspopup="dialog" aria-expanded={visible} title="Show profile"
      onClick={toggle} onKeyDown={(e) => { if (e.target !== e.currentTarget) return; if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPinned((v) => !v) } }}
      onMouseEnter={enter} onMouseLeave={leave}>
      {children}
      {visible && createPortal(
        <span className={`profile-card ${pinned ? 'pinned' : ''}`} role="dialog" aria-label={`Profile of ${name}`} ref={card} style={{ top: pos?.top ?? 8, left: pos?.left ?? 8, visibility: pos ? 'visible' : 'hidden' }}
          onMouseEnter={cancel} onMouseLeave={leave} onClick={(e) => e.stopPropagation()}>
          <span className="profile-card-head"><Avatar avatar={user.avatar} name={name} size={56} /><span><strong>{name}</strong><br /><a className="small" href={`mailto:${user.email}`}>{user.email}</a></span></span>
          {user.bio ? <span className="profile-card-bio">{user.bio}</span> : <span className="muted small">No bio yet.</span>}
          {(profileAreas(user).length || user.programs?.length) ? <span className="small">{[profileAreas(user).join(', '), user.programs?.join(', ')].filter(Boolean).join(' · ')}</span> : null}
          <span className="muted tiny">Joined {fmtDay(user.created_at)}{pinned ? ' · click outside or press Esc to close' : ''}</span>
        </span>, document.body)}
    </span>
  )
}
